import React from "react";
import { CheckOutlined } from "@ant-design/icons";
import { DefaultOptionType } from "antd/lib/select";
import axios from "axios";

export interface MsEnseignesGroup {
  id: string;
  name: string;
  enseignes: string[];
}

export interface MsEnseignesGroupsData {
  enseignes: string[];
  groups: MsEnseignesGroup[];
}

export const MS_GROUP_VALUE_PREFIX = "__ms_group__:";

let cachedData: MsEnseignesGroupsData | null = null;
let fetchPromise: Promise<MsEnseignesGroupsData> | null = null;

export function clearMsEnseignesGroupsCache() {
  cachedData = null;
  fetchPromise = null;
}

export function fetchMsEnseignesGroupsData(): Promise<MsEnseignesGroupsData> {
  if (cachedData) {
    return Promise.resolve(cachedData);
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = axios
    .get<MsEnseignesGroupsData>("/ms/enseignes/groups")
    .then((response) => {
      const data: MsEnseignesGroupsData = {
        enseignes: (response.data.enseignes ?? []).slice().sort((a, b) => a.localeCompare(b)),
        groups: (response.data.groups ?? []).map((group) => ({
          id: group.id,
          name: group.name,
          enseignes: group.enseignes ?? [],
        })),
      };
      cachedData = data;
      return data;
    })
    .catch((error) => {
      fetchPromise = null;
      console.error("Error loading MS enseignes groups:", error);
      throw error;
    });

  return fetchPromise;
}

export function isMsGroupFullySelected(
  group: MsEnseignesGroup,
  selectedValues: string[]
): boolean {
  return (
    group.enseignes.length > 0 &&
    group.enseignes.every((enseigne) => selectedValues.includes(enseigne))
  );
}

export function isMsOptionChecked(
  value: string,
  selectedValues: string[],
  groups: MsEnseignesGroup[]
): boolean {
  if (value.startsWith(MS_GROUP_VALUE_PREFIX)) {
    const groupId = value.slice(MS_GROUP_VALUE_PREFIX.length);
    const group = groups.find((g) => g.id === groupId);
    return !!group && isMsGroupFullySelected(group, selectedValues);
  }
  return selectedValues.includes(value);
}

export function msOptionLabel(text: string, checked: boolean): React.ReactNode {
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {checked ? (
        <CheckOutlined style={{ marginRight: 6, color: "#1677ff" }} />
      ) : (
        <span style={{ display: "inline-block", width: 14, marginRight: 6 }} />
      )}
      {text}
    </span>
  );
}

/** Live option content so checkmarks update when a group expands to enseignes. */
export function renderMsFilterOption(
  option: { value?: string | number | null; title?: React.ReactNode; label?: React.ReactNode },
  selectedValues: string[],
  groups: MsEnseignesGroup[]
): React.ReactNode {
  const value = String(option.value ?? "");
  const text = String(
    option.title ?? (typeof option.label === "string" ? option.label : value)
  );
  return msOptionLabel(text, isMsOptionChecked(value, selectedValues, groups));
}

export function buildMsFilterOptions(
  enseignes: string[],
  groups: MsEnseignesGroup[]
): DefaultOptionType[] {
  const options: DefaultOptionType[] = [];

  if (groups.length > 0) {
    options.push({
      label: "Groupes",
      options: groups.map((group) => ({
        label: group.name,
        value: `${MS_GROUP_VALUE_PREFIX}${group.id}`,
        title: group.name,
      })),
    });
  }

  options.push({
    label: "Enseignes",
    options: enseignes.map((enseigne) => ({
      label: enseigne,
      value: enseigne,
      title: enseigne,
    })),
  });

  return options;
}

/** Expand selected group headers into their enseignes; keep only real enseigne values. */
export function resolveMsSelectValues(
  values: string[],
  groups: MsEnseignesGroup[]
): string[] {
  const resolved = new Set<string>();

  for (const value of values) {
    if (value.startsWith(MS_GROUP_VALUE_PREFIX)) {
      const groupId = value.slice(MS_GROUP_VALUE_PREFIX.length);
      const group = groups.find((g) => g.id === groupId);
      group?.enseignes.forEach((enseigne) => resolved.add(enseigne));
    } else {
      resolved.add(value);
    }
  }

  return Array.from(resolved);
}
